import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type {
  AccentPreference,
  DateFormatPreference,
  TimeFormatPreference,
  WeekStartPreference,
} from "../lib/preferences";
import { z } from "zod";
import {
  ACCENTS,
  DATE_FORMATS,
  TIME_FORMATS,
  WEEK_STARTS,
  browserDefaults,
} from "../lib/preferences";

export type ThemePreference = "light" | "dark" | "system";

/** Each field is checked on its own, so one bad value does not discard the rest. */
const persistedSchema = z.object({
  theme: z.enum(["light", "dark", "system"]).optional().catch(undefined),
  sidebarExpanded: z.boolean().optional().catch(undefined),
  lastWorkspaceSlug: z.string().nullable().optional().catch(undefined),
  notificationSound: z.boolean().optional().catch(undefined),
  chatSound: z.boolean().optional().catch(undefined),
  accent: z.enum(ACCENTS).optional().catch(undefined),
  dateFormat: z.enum(DATE_FORMATS).optional().catch(undefined),
  timeFormat: z.enum(TIME_FORMATS).optional().catch(undefined),
  weekStart: z.enum(WEEK_STARTS).optional().catch(undefined),
});

interface UiState {
  theme: ThemePreference;
  /** Desktop sidebar expanded (true) or collapsed off-canvas (false). */
  sidebarExpanded: boolean;
  /** Workspace opened most recently, used to pick the landing page after sign-in. */
  lastWorkspaceSlug: string | null;
  /** Whether a new notification plays the notification tone. */
  notificationSound: boolean;
  /** Whether a new chat message in another conversation plays the chat tone. */
  chatSound: boolean;
  /** Accent color for buttons, links and focus rings. */
  accent: AccentPreference;
  dateFormat: DateFormatPreference;
  timeFormat: TimeFormatPreference;
  weekStart: WeekStartPreference;
  setTheme: (theme: ThemePreference) => void;
  setAccent: (accent: AccentPreference) => void;
  setDateFormat: (format: DateFormatPreference) => void;
  setTimeFormat: (format: TimeFormatPreference) => void;
  setWeekStart: (day: WeekStartPreference) => void;
  setSidebarExpanded: (expanded: boolean) => void;
  setLastWorkspaceSlug: (slug: string | null) => void;
  setNotificationSound: (enabled: boolean) => void;
  setChatSound: (enabled: boolean) => void;
}

/** Client-only UI preferences (never server data). Persisted as `tm-ui` in localStorage. */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: "system",
      sidebarExpanded: true,
      lastWorkspaceSlug: null,
      notificationSound: true,
      chatSound: true,
      accent: "indigo",
      ...browserDefaults(),
      weekStart: "monday",
      setTheme: (theme) => {
        set({ theme });
      },
      setAccent: (accent) => {
        set({ accent });
      },
      setDateFormat: (dateFormat) => {
        set({ dateFormat });
      },
      setTimeFormat: (timeFormat) => {
        set({ timeFormat });
      },
      setWeekStart: (weekStart) => {
        set({ weekStart });
      },
      setSidebarExpanded: (sidebarExpanded) => {
        set({ sidebarExpanded });
      },
      setLastWorkspaceSlug: (lastWorkspaceSlug) => {
        set({ lastWorkspaceSlug });
      },
      setNotificationSound: (notificationSound) => {
        set({ notificationSound });
      },
      setChatSound: (chatSound) => {
        set({ chatSound });
      },
    }),
    {
      name: "tm-ui",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        sidebarExpanded: state.sidebarExpanded,
        lastWorkspaceSlug: state.lastWorkspaceSlug,
        notificationSound: state.notificationSound,
        chatSound: state.chatSound,
        accent: state.accent,
        dateFormat: state.dateFormat,
        timeFormat: state.timeFormat,
        weekStart: state.weekStart,
      }),
      // A stored value that is no longer valid (older version, hand edit) falls back to the default.
      merge: (persisted, current) => {
        const saved = persistedSchema.safeParse(persisted);
        if (!saved.success) return current;
        const value = saved.data;
        return {
          ...current,
          theme: value.theme ?? current.theme,
          sidebarExpanded: value.sidebarExpanded ?? current.sidebarExpanded,
          lastWorkspaceSlug: value.lastWorkspaceSlug ?? current.lastWorkspaceSlug,
          notificationSound: value.notificationSound ?? current.notificationSound,
          chatSound: value.chatSound ?? current.chatSound,
          accent: value.accent ?? current.accent,
          dateFormat: value.dateFormat ?? current.dateFormat,
          timeFormat: value.timeFormat ?? current.timeFormat,
          weekStart: value.weekStart ?? current.weekStart,
        };
      },
    },
  ),
);
